define('home/views/ChannelCreateFormView',[
    'underscore',
    'backbone-marionette',
    'handlebars',

    'core/UniqueModel',

    'home/models/Channel',
    'home/collections/CategoryCollection',
    'home/mixins/withChannelAvatar',
    'home/templates/channelCreateForm',
    'home/templates/channelCreateError',
    'home/utils/confirmModal',
], function (
    _,
    Marionette,
    Handlebars,

    UniqueModel,

    Channel,
    CategoryCollection,
    withChannelAvatar,
    channelCreateFormTemplate,
    channelCreateErrorTemplate,
    confirmModal
) {
    'use strict';

    /**
     * A view that contains a form for creating a channel.
     *
     * @param {Object} options - {
     *                               model: new instance of a channel model,
     *                               session: current session
     *                           }
     */
    var ChannelCreateFormView = Marionette.ItemView.extend({
        template: channelCreateFormTemplate,

        templateHelpers: function () {
            return {
                // only logged in verified users can create channels
                sessionUserVerified: this.session.user.get('isVerified'),
                bannerColors: Channel.BANNER_COLORS,
                categories: this.categories.toJSON(),
                isNew: this.isNew,
            };
        },

        events: {
            'submit': 'submitChannel',
            'keyup input[name=name]': 'handleNameKeyup',
            'change select[name=bannerColor]': 'updateBanner',
            'change input[name=banner-type]': 'updateBanner',
            'click input[name=banner-type][value=file]': 'handleBannerTypeFileClick',
            'change input[name=banner-image]': 'handleBannerImageChange',
            'click input[name=avatarType]': 'handleAvatarTypeFileClick',
            'change input[name=avatar]': 'handleAvatarImageChanage',
            'change select[name=primary-category]': 'handlePrimaryCategoryChange',
            'click [data-action=cancel-create]': 'navigateBack',
        },

        ui: {
            name: '[name=name]',
            bannerColor: '[name=bannerColor]',
            description: '[name=description]',
            avatar: '[name=avatar]',
            bannerImage: '[name=banner-image]',
            defaultAvatar: '[data-role=default-avatar]',
            form: '[data-role=channel-form]',
            buttons: '[data-role=channel-form-button]',
            primaryCategory: '[name=primary-category]',
        },

        initialize: function (options) {
            // Used if user cancels changes
            this.modelBackup = _.clone(this.model.attributes);

            this.session = options.session;
            this.categories = CategoryCollection.get();
            this.isNew = this.model.isNew();
            this.listenTo(this.model, 'invalid', this.handleValidationError);
            this.listenTo(this.model, 'change:bannerColor change:name', this.updateDefaultAvatar);
            this.listenTo(this.categories, 'sync add remove', this.render);
        },

        onClose: function () {
            this.cancelChanges();
        },

        updateDefaultAvatar: function () {
            var name = this.model.get('name') || '';
            var bannerColor = this.model.get('bannerColor');

            this.ui.defaultAvatar.html(Handlebars.partials.defaultChannelAvatar({
                bannerColorClass: bannerColor && ('-' + bannerColor),
                channelInitial: name.charAt(0),
            }));
        },

        /**
         * Returns the model attributes for the Category with the given slug.
         * At this point, the category models should exist and be fetched,
         * because the CategoryCollection was fetched during initialization.
         *
         * @param {string} slug - The slug for the category to return
         * @returns {Object} - Attributes for the specified category
         */
        getCategoryAttrs: function (slug) {
            if (slug)
                return new UniqueModel(Channel, { slug: slug }).attributes;
        },

        submitChannel: function (evt) {
            evt.preventDefault();

            // hide all displayed errors
            this.$('.form-attribute-input').removeClass('is-error');

            this.disableForm();

            var messageName = this.isNew ? 'created' : 'updated';

            var data = {
                name: this.isNew ? this.ui.name.val() : this.model.get('name'),
                bannerColor: this.ui.bannerColor.val(),
                description: this.ui.description.val(),

                // To update the related category models on the channel, update the
                // attribute, which change will trigger the channel model to update
                // the related category models. The attribute expects a plain object
                // value like it would receive from the server, not a Backbone Model.
                //
                // To clear out an existing related category, pass null, not undefined.
                // This is so that a change event will occur on the attribute, which
                // should previously have been undefined, since the category details
                // are stored in the related models, not in attributes.
                primaryCategory: this.getCategoryAttrs(this.ui.primaryCategory.val()) || null,
            };

            var avatarType = this.$('[name=avatarType]:checked').val();

            if (avatarType === 'file') {
                var avatarImageFile = this.getAvatarImageFile();
                if (avatarImageFile)
                    data.avatar = avatarImageFile;
            }

            var bannerType = this.getBannerType();
            var bannerImageFile = bannerType === 'file' && this.getBannerImageFile();
            if (bannerImageFile)
                data.banner = bannerImageFile;

            this.model.save(data, {
                avatarType: avatarType,
                bannerType: bannerType,
                success: _.bind(this.handleSaveSuccess, this, messageName),
                error: _.bind(this.handleSaveError, this),
            });
        },

        handleSaveSuccess: function (messageName, model) {
            this.modelBackup = _.clone(this.model.attributes);
            this.enableForm();
            this.trigger(messageName, model);
        },

        handleNameKeyup: function () {
            var channelName = this.ui.name.val().trim();
            this.model.set('name', channelName);
        },

        getBannerType: function () {
            return this.$('[name=banner-type]:checked').val();
        },

        getBannerImageFile: function () {
            return this.ui.bannerImage[0].files[0];
        },

        getAvatarImageFile: function () {
            return this.ui.avatar[0].files[0];
        },

        bannerImageExists: function () {
            return Boolean(this.getBannerImageFile() ||
                this.model.get('banner') ||
                this.modelBackup.banner ||

                // Legacy support
                this.model.get('options').alertBackground ||
                this.modelBackup.options.alertBackground);
        },

        avatarImageExists: function () {
            return Boolean(this.getAvatarImageFile() ||
                this.model.get('avatar') ||
                this.modelBackup.avatar ||

                // Legacy support
                this.model.get('options').favicon ||
                this.modelBackup.options.favicon);
        },

        /**
         * Called when user tries to select the banner image radio button.
         * If there's no file previously chosen, brings up the file chooser
         * and prevents the radio button from being selected. This is done to
         * prevent the invalid state where the image radio button is selected
         * but there's no file chosen.
         *
         * @returns {boolean} - Returns false if user action is invalid to prevent
         *                      the UI change.
         */
        handleBannerTypeFileClick: function () {
            if (!this.bannerImageExists()) {
                this.ui.bannerImage.click();
                return false;
            }

            this.updateBanner();
        },

        /**
         * Called when user tries to select the avatar image radio button.
         * If there's no file previously chosen, brings up the file chooser
         * and prevents the raiod button from being selected. This is done to
         * prevent the invalid state where the image radio button is selected
         * but there's no file chosen.
         *
         * @returns {boolean} - Returns false if user action is invalid to prevent
         *                      the UI change.
         */
        handleAvatarTypeFileClick: function () {
            if (!this.avatarImageExists()) {
                this.ui.avatar.click();
                return false;
            }
        },

        /**
         * Called when a banner image file is selected. Automatically selects the
         * correct banner type radio button depending on if a file was chosen or
         * not, to prevent the invalid state of having the file radio button selected
         * with no file chosen.
         */
        handleBannerImageChange: function () {
            var shouldSelectFileRadio = Boolean(this.bannerImageExists());
            this.$('input[name=banner-type][value=file]').prop('checked', shouldSelectFileRadio);
            this.$('input[name=banner-type][value=color]').prop('checked', !shouldSelectFileRadio);
            this.updateBanner();
        },

        /**
         * Called when an avatar image file is selected. Automatically selects the
         * correct avatar type radio button depending on if a file was chosen or not,
         * to prevent the invalid state of having the file radio button selected with
         * no file chosen.
         */
        handleAvatarImageChanage: function () {
            var shouldSelectFileRadio = Boolean(this.avatarImageExists());
            this.$('input[name=avatarType][value=file]').prop('checked', shouldSelectFileRadio);
            this.$('input[name=avatarType][value=default]').prop('checked', !shouldSelectFileRadio);
        },

        /**
         * Rerenders the banner to show either the channel color or the image placeholder,
         * depending on which type is selected.
         */
        updateBanner: function () {
            // If user selected banner type file, show a preview.
            var bannerImage;
            if (this.getBannerType() === 'file') {
                // If user hasn't selected a new banner image, they may have a previously uploaded banner image.
                // Get it from the backup attrs that haven't been mutated, in case the user cleared out the
                // previous image on the current model.
                bannerImage = this.getBannerImageFile() ||
                    this.modelBackup.banner ||
                    this.modelBackup.options.alertBackground;
            }

            this.model.set({
                bannerColor: this.ui.bannerColor.val(),
                banner: bannerImage,
            });
        },

        cancelChanges: function () {
            // Revert all the changes made during editing
            this.model.set(this.modelBackup);
        },

        navigateBack: function () {
            this.cancelChanges();
            window.history.back();
        },

        handleValidationError: function (_model, errs) {
            this.enableForm();

            _.each(errs, function (err) {
                this.$('.form-attribute-input[data-attribute*=' + err.attrName + ']')
                    .addClass('is-error')
                    .find('[data-role=attribute-error]')
                    .text(err.message);
            }, this);
        },

        handleSaveError: function (_channel, response) {
            this.enableForm();

            confirmModal.confirmationPromise(
                confirmModal.TYPES.CLOSE,
                channelCreateErrorTemplate(response)
            );
        },

        enableForm: function () {
            this.ui.form.removeClass('is-submitting');
            this.ui.buttons.removeAttr('disabled');
        },

        disableForm: function () {
            this.ui.form.addClass('is-submitting');
            this.ui.buttons.attr('disabled', 'disabled');
        },

    });

    withChannelAvatar.call(ChannelCreateFormView.prototype);

    return ChannelCreateFormView;
});

